<template>
  <div
    :class="`${$options.name}`"
    class="tw:flex tw:flex-col tw:gap-2 tw:p-1 tw:text-black tw:w-56 tw:max-h-[calc(100vh_-_6.25rem)]"
  >
    <p class="tw:caption tw:!text-default-700">{{ trans._menu.header.workspace.workspace }}</p>
    <EdroneRadioButton
      :checked="true"
      :label="user.companyUrl"
      controlled
      unique-id="main-menu__workspace-select--current"
      @click="handleClickCurrentWorkspace"
    />
    <div>
      <div class="tw:body-2 tw:flex tw:gap-2 tw:border-b tw:border-default-200 tw:items-center tw:h-10">
        <span class="tw:!text-default-700">{{ trans._menu.header.workspace.workspaceId }}:</span>
        <span class="tw:ms-auto">{{ trackerId }}</span>
        <EdroneTooltip
          :offset="[0, 8]"
          close-trigger-class-name="copied_confirmation"
          position="top"
          trigger-event="click"
          type="custom"
        >
          <div
            class="tw:w-6 tw:h-6 tw:border tw:border-default-200 tw:rounded-sm tw:flex tw:justify-center tw:items-center tw:cursor-pointer tw:select-none tw:hover:border-default-400"
            @click="
              handleClickCopy(trackerId);
              gtagUniversalSend({ component_id: 'main-menu__workspace-copy-button--trackerId' });
            "
          >
            <font-awesome-icon icon="fa-light fa-clone" />
          </div>
          <template #custom>
            <span class="copied_confirmation tw:caption tw:!text-default-700">{{ copied + " 😊" }}</span>
          </template>
        </EdroneTooltip>
      </div>
      <div class="tw:body-2 tw:flex tw:gap-2 tw:border-b tw:border-default-200 tw:items-center tw:h-10">
        <span class="tw:!text-default-700">{{ trans._menu.header.workspace.appId }}:</span>
        <span class="tw:ms-auto">{{ appId }}</span>
        <EdroneTooltip
          :offset="[0, 8]"
          close-trigger-class-name="copied_confirmation"
          position="top"
          trigger-event="click"
          type="custom"
        >
          <div
            class="tw:w-6 tw:h-6 tw:border tw:border-default-200 tw:rounded-sm tw:flex tw:justify-center tw:items-center tw:cursor-pointer tw:select-none tw:hover:border-default-400"
            @click="
              handleClickCopy(appId);
              gtagUniversalSend({ component_id: 'main-menu__workspace-copy-button--appId' });
            "
          >
            <font-awesome-icon icon="fa-light fa-clone" />
          </div>
          <template #custom>
            <span class="copied_confirmation tw:caption tw:!text-default-700">{{ copied + " 😊" }}</span>
          </template>
        </EdroneTooltip>
      </div>
      <div class="tw:body-2 tw:flex tw:gap-2 tw:border-b tw:border-default-200 tw:items-center tw:h-10">
        <span class="tw:!text-default-700">{{ trans._menu.header.workspace.agentId }}:</span>
        <span class="tw:ms-auto">{{ user.agentId }}</span>
        <EdroneTooltip
          :offset="[0, 8]"
          close-trigger-class-name="copied_confirmation"
          position="top"
          trigger-event="click"
          type="custom"
        >
          <div
            class="tw:w-6 tw:h-6 tw:border tw:border-default-200 tw:rounded-sm tw:flex tw:justify-center tw:items-center tw:cursor-pointer tw:select-none tw:hover:border-default-400"
            @click="
              handleClickCopy(user.agentId);
              gtagUniversalSend({ component_id: 'main-menu__workspace-copy-button--agentId' });
            "
          >
            <font-awesome-icon icon="fa-light fa-clone" />
          </div>
          <template #custom>
            <span class="copied_confirmation tw:caption tw:!text-default-700">{{ copied + " 😊" }}</span>
          </template>
        </EdroneTooltip>
      </div>
    </div>

    <template v-if="trackers.length">
      <p class="tw:caption tw:!text-default-700">{{ trans._menu.header.workspace.changeWorkspace }}</p>
      <div :class="$options.name + '__trackers'" class="tw:flex tw:flex-col tw:gap-2 tw:w-full tw:overflow-y-auto">
        <EdroneRadioButton
          v-for="tracker in trackers"
          :key="tracker.trackerId"
          :disabled="changingWorkspace"
          :label="tracker.url"
          :unique-id="`main-menu__workspace-select--${tracker.trackerId}`"
          controlled
          @click="handleClickSelectWorkspace(tracker.trackerId)"
        />
      </div>
    </template>

    <EdroneButton
      :disabled="isDemo || changingWorkspace"
      class="tw:w-full"
      type="text"
      unique-id="main-menu__workspace-add-button"
      @click="handleClickAddWorkspace"
    >
      <template #left-icon>
        <font-awesome-icon icon="fa-light fa-plus" />
      </template>
      {{ trans._menu.header.workspace.addWorkspace }}
    </EdroneButton>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import EdroneRadioButton from "edrone-components/EdroneRadioButton.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { changeWorkspace } from "vue-app/api/menu/account.ts";
import { copied, handleClickCopy } from "edrone-components/EdroneInput.helpers.ts";
import { useStore } from "vuex";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "MainMenuWorkspaceContent",
  components: {
    EdroneButton,
    EdroneRadioButton,
    EdroneTooltip,
  },
  setup() {
    const store = useStore();
    const trackerId = window.EDRONE_CONTEXT.trackerId;
    const appId = window.EDRONE_CONTEXT.appId;
    const user = window.EDRONE_CONTEXT.user;
    const trackers = computed(() =>
      store.getters["global/trackers"].filter(
        (tracker: { trackerId: number; url: string }) => tracker.trackerId != trackerId
      )
    );
    const isDemo = window.EDRONE_CONTEXT.isDemo;

    function handleClickAddWorkspace() {
      window.location.href = "/login/new-workspace";
    }

    const changingWorkspace = ref(false);
    function handleClickSelectWorkspace(trackerId: number) {
      changingWorkspace.value = true;
      changeWorkspace(trackerId);
    }

    function handleClickCurrentWorkspace() {
      window.open(user.companyUrl, "_blank");
    }

    return {
      handleClickCopy,
      copied,
      trackerId,
      appId,
      user,
      trackers,
      isDemo,
      handleClickAddWorkspace,
      handleClickSelectWorkspace,
      handleClickCurrentWorkspace,
      changingWorkspace,
      gtagUniversalSend,
    };
  },
});
</script>

<style lang="scss" scoped>
.MainMenuWorkspaceContent {
  /* scrollbar */
  &__trackers::-webkit-scrollbar {
    display: none;
  }

  &__trackers {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}

.MainMenuMobile .MainMenuWorkspaceContent {
  width: 100%;
  max-height: 50%;
}
</style>
